import {
  getDesktopChatAuthScope,
  type DesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import type { InputBarRoutingIntent } from "@/stores/input-bar-target-store";

export const PROMPT_RETRY_TTL_MS = 10 * 60_000;
const MAX_IDENTITIES = 50;
const STORAGE_PREFIX = "denker.prompt-retry.";

export type PromptRetryStatus = "pending" | "timed_out" | "late_accepted";

export interface PromptRetryIdentity {
  version: 1;
  authScope?: DesktopChatAuthScope;
  requestId: string;
  composerId: string;
  routingIntent: InputBarRoutingIntent | null;
  status: PromptRetryStatus;
  createdAt: number;
  expiresAt: number;
}

const identities = new Map<string, PromptRetryIdentity>();

function storageKey(retryKey: string): string {
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < retryKey.length; index += 1) {
    const code = retryKey.charCodeAt(index);
    first = Math.imul(first ^ code, 0x01000193);
    second = Math.imul(second ^ code, 0x85ebca6b);
  }
  return `${STORAGE_PREFIX}${(first >>> 0).toString(16)}${(second >>> 0).toString(16)}`;
}

function isRoutingIntent(
  value: unknown,
): value is InputBarRoutingIntent | null {
  if (value === null) return true;
  if (!value || typeof value !== "object") return false;
  const intent = value as Partial<InputBarRoutingIntent>;
  const target = intent.target as
    | Partial<NonNullable<InputBarRoutingIntent["target"]>>
    | null
    | undefined;
  const targetIsValid =
    target === null ||
    (!!target &&
      typeof target.conversationId === "string" &&
      typeof target.projectAgentId === "string" &&
      typeof target.agentName === "string" &&
      (target.ticket === null || typeof target.ticket === "string"));
  return (
    typeof intent.startNewConversation === "boolean" &&
    (intent.intentId === null || typeof intent.intentId === "string") &&
    targetIsValid
  );
}

function isIdentity(
  value: unknown,
  now = Date.now(),
): value is PromptRetryIdentity {
  if (!value || typeof value !== "object") return false;
  const identity = value as Partial<PromptRetryIdentity>;
  return (
    identity.version === 1 &&
    typeof identity.requestId === "string" &&
    identity.requestId.length > 0 &&
    typeof identity.composerId === "string" &&
    identity.composerId.length > 0 &&
    ["pending", "timed_out", "late_accepted"].includes(identity.status ?? "") &&
    Number.isFinite(identity.createdAt) &&
    Number.isFinite(identity.expiresAt) &&
    (identity.expiresAt ?? 0) > now &&
    isRoutingIntent(identity.routingIntent)
  );
}

function matchesCurrentAuthScope(identity: PromptRetryIdentity): boolean {
  const scope = getDesktopChatAuthScope();
  return (
    identity.authScope?.accountId === scope?.accountId &&
    identity.authScope?.epoch === scope?.epoch
  );
}

function removeItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Persistence is best-effort.
  }
}

function prune(now = Date.now()): void {
  const valid = [...identities.entries()].filter((entry) => {
    if (isIdentity(entry[1], now)) return true;
    identities.delete(entry[0]);
    return false;
  });
  valid
    .sort((first, second) => second[1].createdAt - first[1].createdAt)
    .slice(MAX_IDENTITIES)
    .forEach(([key]) => identities.delete(key));
  if (typeof window === "undefined") return;
  try {
    const stored: Array<{ key: string; createdAt: number }> = [];
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(localStorage.getItem(key) ?? "null");
      } catch {
        parsed = null;
      }
      if (!isIdentity(parsed, now)) removeItem(key);
      else stored.push({ key, createdAt: parsed.createdAt });
    }
    stored
      .sort((first, second) => second.createdAt - first.createdAt)
      .slice(MAX_IDENTITIES)
      .forEach(({ key }) => removeItem(key));
  } catch {
    // Storage enumeration can be blocked.
  }
}

export function readRetryIdentity(
  retryKey: string,
): PromptRetryIdentity | undefined {
  const inMemory = identities.get(retryKey);
  if (inMemory && isIdentity(inMemory) && matchesCurrentAuthScope(inMemory))
    return inMemory;
  identities.delete(retryKey);
  if (typeof window === "undefined") return undefined;
  const key = storageKey(retryKey);
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "null");
    if (!isIdentity(parsed) || !matchesCurrentAuthScope(parsed)) {
      removeItem(key);
      return undefined;
    }
    identities.set(retryKey, parsed);
    return parsed;
  } catch {
    removeItem(key);
    return undefined;
  }
}

export function storeRetryIdentity(
  retryKey: string,
  identity: PromptRetryIdentity,
): void {
  identity = { ...identity, authScope: getDesktopChatAuthScope() ?? undefined };
  identities.set(retryKey, identity);
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(storageKey(retryKey), JSON.stringify(identity));
    } catch {
      // The in-memory identity still protects this webview.
    }
  }
  prune();
}

export function clearRetryIdentity(retryKey: string, requestId: string): void {
  if (identities.get(retryKey)?.requestId === requestId)
    identities.delete(retryKey);
  for (const [key, identity] of identities) {
    if (identity.requestId === requestId) identities.delete(key);
  }
  if (typeof window === "undefined") return;
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      const stored = JSON.parse(
        localStorage.getItem(key) ?? "null",
      ) as Partial<PromptRetryIdentity> | null;
      if (stored?.requestId === requestId) removeItem(key);
    }
  } catch {
    removeItem(storageKey(retryKey));
  }
}

export function clearRetryIdentitiesForComposer(composerId: string): void {
  for (const [key, identity] of identities) {
    if (identity.composerId === composerId) identities.delete(key);
  }
  if (typeof window === "undefined") return;
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      try {
        const parsed = JSON.parse(
          localStorage.getItem(key) ?? "null",
        ) as Partial<PromptRetryIdentity> | null;
        if (!parsed || parsed.composerId === composerId) removeItem(key);
      } catch {
        removeItem(key);
      }
    }
  } catch {
    // Editing must continue when storage is blocked.
  }
}

export function isRetryRequestRetained(requestId: string): boolean {
  return [...identities.values()].some(
    (identity) => identity.requestId === requestId,
  );
}

export function clearAllRetryIdentities(): void {
  identities.clear();
  if (typeof window === "undefined") return;
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) removeItem(key);
    }
  } catch {
    // Tests may deliberately block Storage.
  }
}

export const resetRetryIdentityStoreForTests = clearAllRetryIdentities;

/** A mirror's first scope hydration must preserve the active owner's retries. */
export function clearRetryIdentitiesForAuthChange(): void {
  if (!getDesktopChatAuthScope()) {
    clearAllRetryIdentities();
    return;
  }
  for (const [key, identity] of identities) {
    if (!matchesCurrentAuthScope(identity)) identities.delete(key);
  }
  if (typeof window === "undefined") return;
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(localStorage.getItem(key) ?? "null");
      } catch {
        parsed = null;
      }
      if (!isIdentity(parsed) || !matchesCurrentAuthScope(parsed))
        removeItem(key);
    }
  } catch {
    // Storage can be unavailable during sign-in; reads still validate scope.
  }
}
